import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Store,
  Trash2,
  RefreshCw,
  Tag,
  Mail,
  User,
  Power,
  PowerOff,
  ExternalLink,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminMerchants({ user }) {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, merchant: null });
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/merchants`, {
        withCredentials: true,
      });
      setMerchants(response.data || []);
    } catch (error) {
      console.error("Error fetching merchants:", error);
      toast.error("Failed to load merchants");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.merchant) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/merchants/${deleteDialog.merchant.id}`, {
        withCredentials: true,
      });
      toast.success("Merchant and all their discounts deleted");
      setDeleteDialog({ open: false, merchant: null });
      fetchMerchants();
    } catch (error) {
      console.error("Error deleting merchant:", error);
      toast.error("Failed to delete merchant");
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (merchant) => {
    setToggling(merchant.id);
    try {
      const response = await axios.put(
        `${API}/admin/merchants/${merchant.id}/toggle-status`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchMerchants();
    } catch (error) {
      console.error("Error toggling merchant status:", error);
      toast.error("Failed to update merchant status");
    } finally {
      setToggling(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unknown";
    return new Date(dateStr).toLocaleDateString("en-SG", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Store className="h-6 w-6 text-emerald-400" />
                Merchant Management
              </h1>
              <p className="text-slate-400 text-sm">
                View and manage registered merchants
              </p>
            </div>
          </div>
          <Button onClick={fetchMerchants} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-white">{merchants.length}</div>
            <div className="text-sm text-slate-400">Total Merchants</div>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-emerald-400">
              {merchants.filter((m) => m.is_active !== false).length}
            </div>
            <div className="text-sm text-slate-400">Active</div>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-red-400">
              {merchants.filter((m) => m.is_active === false).length}
            </div>
            <div className="text-sm text-slate-400">Inactive</div>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-amber-400">
              {merchants.reduce((acc, m) => acc + (m.discount_count || 0), 0)}
            </div>
            <div className="text-sm text-slate-400">Total Discounts</div>
          </Card>
        </div>

        {/* Merchants Table */}
        <Card className="bg-slate-800/50 border-slate-700">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : merchants.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Store className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No merchants registered</p>
              <p className="text-sm mt-2">Merchants can apply via the Merchant Application page</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Business</TableHead>
                  <TableHead className="text-slate-400">Owner</TableHead>
                  <TableHead className="text-slate-400">Category</TableHead>
                  <TableHead className="text-slate-400">Discounts</TableHead>
                  <TableHead className="text-slate-400">Created</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {merchants.map((merchant) => (
                  <TableRow key={merchant.id} className="border-slate-700">
                    <TableCell>
                      <div className="font-medium text-white">{merchant.business_name}</div>
                      {merchant.website && (
                        <a
                          href={merchant.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-amber-400 hover:underline flex items-center gap-1 mt-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Website
                        </a>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-300">
                        <User className="h-3 w-3" />
                        <span className="text-sm">{merchant.user_name || "Unknown"}</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-400 mt-1">
                        <Mail className="h-3 w-3" />
                        <span className="text-xs">{merchant.user_email || "Unknown"}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {merchant.category ? (
                        <Badge className="bg-slate-700 text-slate-300">
                          {merchant.category}
                        </Badge>
                      ) : (
                        <span className="text-slate-500 text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Link to="/admin/merchant-discounts">
                        <div className="flex items-center gap-1 text-amber-400 hover:text-amber-300 cursor-pointer">
                          <Tag className="h-4 w-4" />
                          <span className="font-semibold">{merchant.discount_count || 0}</span>
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-400">
                        {formatDate(merchant.created_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {merchant.is_active === false ? (
                        <Badge className="bg-red-500/20 text-red-400">Inactive</Badge>
                      ) : (
                        <Badge className="bg-emerald-500/20 text-emerald-400">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(merchant)}
                          disabled={toggling === merchant.id}
                          className={`h-8 w-8 p-0 ${
                            merchant.is_active === false
                              ? "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                              : "text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                          }`}
                          title={merchant.is_active === false ? "Activate" : "Deactivate"}
                        >
                          {toggling === merchant.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : merchant.is_active === false ? (
                            <Power className="h-4 w-4" />
                          ) : (
                            <PowerOff className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteDialog({ open: true, merchant })}
                          className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          title="Delete merchant"
                          data-testid={`delete-merchant-${merchant.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={deleteDialog.open}
          onOpenChange={(open) => setDeleteDialog({ open, merchant: open ? deleteDialog.merchant : null })}
        >
          <AlertDialogContent className="bg-slate-900 border-slate-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Merchant?</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Are you sure you want to permanently delete{" "}
                <span className="text-amber-400 font-semibold">
                  "{deleteDialog.merchant?.business_name}"
                </span>
                ?
                <br />
                <br />
                <span className="text-red-400 font-semibold">
                  This will also delete all {deleteDialog.merchant?.discount_count || 0} discounts created by this merchant.
                </span>
                <br />
                <br />
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                Delete Merchant
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
